import { useEffect, useRef, useState } from "react"
import { cn } from "../utils"

interface NumberTickerProps {
	value: number
	className?: string
	format?: (value: number) => string
	duration?: number
	decimals?: number
}

/**
 * A lightweight component that animates a number from its previous value to a new value.
 * Uses requestAnimationFrame for 60fps performance without external animation libraries.
 */
export function NumberTicker({
	value,
	className,
	format,
	duration = 800,
	decimals = 2,
}: NumberTickerProps) {
	const [displayValue, setDisplayValue] = useState(value)
	const valueRef = useRef(value)
	const startTimeRef = useRef<number | null>(null)
	const startValueRef = useRef(value)
	const animationFrameRef = useRef<number | null>(null)

	// Default formatter if none provided
	const defaultFormat = (num: number) =>
		new Intl.NumberFormat("en-US", {
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		}).format(num)

	const formatter = format || defaultFormat

	useEffect(() => {
		// If value hasn't changed, do nothing
		if (value === valueRef.current) {
			return
		}

		// Start animation from current display value to new value
		startValueRef.current = displayValue
		valueRef.current = value
		startTimeRef.current = null

		const animate = (timestamp: number) => {
			if (!startTimeRef.current) {
				startTimeRef.current = timestamp
			}
			const progress = timestamp - startTimeRef.current
			const t = Math.min(progress / duration, 1)

			// Ease out cubic
			const easeOutCubic = 1 - (1 - t) ** 3

			const current =
				startValueRef.current + (valueRef.current - startValueRef.current) * easeOutCubic

			setDisplayValue(current)

			if (t < 1) {
				animationFrameRef.current = requestAnimationFrame(animate)
			} else {
				setDisplayValue(value) // Ensure we land exactly on the target
			}
		}

		animationFrameRef.current = requestAnimationFrame(animate)

		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current)
			}
		}
	}, [value, duration, displayValue])

	return (
		<span className={cn("font-mono tabular-nums tracking-tight", className)}>
			{formatter(displayValue)}
		</span>
	)
}
